import type { ComputedRef } from 'vue'
import { computed, unref } from 'vue'

import { useMutation, useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

import { queryClient } from '@/lib/queryClient'
import { useAuthStore } from '@/store/auth'
import { useChatStore } from '@/store/chat'
import { useToast } from '@/store/toast'

import { handleApiErrorToast, isGptAccessRevokedAxiosError } from '..'
import { removeChatFromPinnedCache } from './chatPinCacheUtils'
import { chatHtmlPagesQueryKey, chatWebsiteQueryOptions } from './chatQueryKeys'
import { useNewChatMutation } from './useNewChatMutation'
import { useSendMessageMutation } from './useSendMessageMutation'
import { ChatApi } from '../modules/chat'

import type { UpdateChatData } from './chatQueryTypes'
import type {
  IChat,
  IChatListFilters,
  IChatListItem,
  IHtmlPageDescriptor,
  IHtmlPagesResponse,
  IPaginatedChatListResponse,
} from '../types/chat'
import type { IWebsiteSummary } from '../types/websites'

/**
 * The chat query layer's shared pieces, re-exported so every consumer keeps importing
 * them from this module: the split below is an internal one.
 *
 * - `./chatQueryKeys` — query keys, options and imperative reads
 * - `./chatQueryTypes` — mutation payload and stream-state types
 * - `./chatQueryCacheUtils` — patches applied to the cached chat lists
 * - `./chatStreamHandlers` — SSE event handling shared by the two streaming mutations
 * - `./useNewChatMutation`, `./useSendMessageMutation` — the streaming mutations
 * - `./chatPlaceholderChat` — the optimistic chat shown while POST `/chats` is in flight
 */
export * from './chatQueryKeys'
export * from './chatQueryTypes'

/**
 * Vue Query + Pinia helpers for chat CRUD and SSE-backed create/send flows.
 *
 * @returns Query/mutation factories: `list`, `get`, `newChat`, `sendMessage`, `update`, `remove`
 */
export const useChatQueries = () => {
  const router = useRouter()
  const { isAuthenticated } = storeToRefs(useAuthStore())

  /**
   * Factory for POST `/chats` with SSE: creates the chat, navigates (unless draft), streams the reply.
   * Registers {@link StreamState} on the chat store so the UI can stop the stream from the sidebar/topbar.
   *
   * Non-draft sends jump to the chat view straight away, on a placeholder chat, and
   * only swap to the real id once `chat_created` lands — the request is no longer
   * something the user waits through on the home page.
   *
   * @returns `create` mutation and reactive `streamState`
   */
  const newChat = () => useNewChatMutation(router)

  /**
   * Factory for POST `/chats/:id/messages` with SSE: optimistic user row, then streamed assistant reply.
   *
   * @returns `send` mutation and reactive `streamState`
   */
  const sendMessage = useSendMessageMutation

  /**
   * Paginated chat list; key includes `filters` when provided. Page 1 also carries the
   * caller's pinned chats — see {@link IPaginatedChatListResponse}; read those through
   * the chat store's `pinnedChats`, which is what the "Pinned" section renders.
   */
  const list = (filters?: IChatListFilters) =>
    useQuery<IPaginatedChatListResponse>({
      queryKey: computed(() => {
        const filtersValue = unref(filters)
        return filtersValue
          ? (['chats', 'list', filters] as const)
          : (['chats', 'list'] as const)
      }),
      queryFn: () => ChatApi.list(filters).catch(handleApiErrorToast),
      enabled: isAuthenticated,
    })

  /**
   * Loads a single chat by id; disabled until `id` is truthy. On 404, toasts and redirects to new chat.
   */
  const get = (id?: ComputedRef<string | undefined>) => {
    return useQuery<IChat, Error, IChat>({
      queryKey: computed(() => {
        const idValue = unref(id)
        return idValue ? ['chats', 'get', idValue] : ['chats', 'get']
      }),
      queryFn: () => {
        const idValue = unref(id)
        if (!idValue) {
          throw new Error('ID is required')
        }
        return ChatApi.get(idValue).catch((error) => {
          useToast().onError('Conversation not found')
          router.push({ name: 'NewChat' })
          throw error
        })
      },
      enabled: computed(() => isAuthenticated.value && !!unref(id)),
    })
  }

  /**
   * All generated HTML page descriptors for a chat (EVGPT-1565), in document order.
   * Drives the preview HTML/website selector so it lists every page across the whole
   * chat, not just those in loaded messages. Disabled until `id` is truthy; `select`
   * unwraps to the `pages` array.
   */
  const htmlPages = (id?: ComputedRef<string | undefined>) =>
    useQuery<IHtmlPagesResponse, Error, IHtmlPageDescriptor[]>({
      queryKey: computed(() => chatHtmlPagesQueryKey(unref(id))),
      queryFn: () => {
        const idValue = unref(id)
        if (!idValue) throw new Error('ID is required')
        return ChatApi.htmlPages(idValue)
      },
      select: (data) => data.pages,
      enabled: computed(() => isAuthenticated.value && !!unref(id)),
    })

  /**
   * The chat's website (one website per chat), or `null` when it has none — the
   * authoritative answer the UI used to derive by scanning HTML blocks for
   * `file.metadata.website_id`. Disabled until `id` is truthy; publish / unpublish /
   * delete seed or invalidate it (see {@link chatWebsiteQueryKey}) so the resolved
   * website never reads a stale publish state.
   */
  const website = (id?: ComputedRef<string | undefined>) =>
    useQuery<IWebsiteSummary | null>(() => ({
      ...chatWebsiteQueryOptions(unref(id)),
      enabled: isAuthenticated.value && !!unref(id),
    }))

  /** PATCH chat metadata; syncs Pinia and invalidates `['chats']` queries. */
  const update = useMutation<IChat, Error, UpdateChatData>({
    mutationFn: ({ chatId, chat }) => ChatApi.update(chatId, chat),
    onSuccess: (updatedChat) => {
      // Keep store in sync for immediate UI updates
      useChatStore().setChat(updatedChat)
      // Refresh any chat list/get queries
      queryClient.invalidateQueries({ queryKey: ['chats'] })
    },
    onError: (error, variables) => {
      if (isGptAccessRevokedAxiosError(error)) {
        useChatStore().markChatGptAccessRevoked(variables.chatId)
        queryClient.invalidateQueries({
          queryKey: ['chats', 'get', variables.chatId],
        })
      }
      handleApiErrorToast(
        error as unknown as Parameters<typeof handleApiErrorToast>[0],
      )
    },
  })

  /**
   * POST `/chats/:id/extend-retention`; resets `updated_at` to now (server-side), then syncs
   * Pinia and invalidates `['chats']` so the chat drops out of the sidebar's "Expiring soon".
   */
  const extendRetention = useMutation<IChat, Error, string>({
    mutationFn: (chatId: string) => ChatApi.extendRetention(chatId),
    onSuccess: (updatedChat) => {
      useChatStore().setChat(updatedChat)
      queryClient.invalidateQueries({ queryKey: ['chats'] })
    },
    onError: (error) => {
      handleApiErrorToast(
        error as unknown as Parameters<typeof handleApiErrorToast>[0],
      )
    },
  })

  /** DELETE chat by id; invalidates `['chats']` on success. */
  const remove = useMutation<void, Error, string>({
    mutationFn: (chatId: string) => ChatApi.remove(chatId),
    onSuccess: (_, chatId) => {
      // Drop it from the pinned section immediately — the invalidation below refetches
      // the pinned query too, but not before the row would flash as a dead entry.
      removeChatFromPinnedCache(chatId)
      queryClient.invalidateQueries({ queryKey: ['chats'] })
    },
    onError: (error) => {
      handleApiErrorToast(
        error as unknown as Parameters<typeof handleApiErrorToast>[0],
      )
    },
  })

  /**
   * PATCH `/chats/pins` with the complete pinned list, topmost first. Pinning, unpinning
   * and dragging the section into a new order are all this one call — the endpoint
   * replaces the stored list rather than toggling a chat — so callers pass the list they
   * want to end up with. Being declarative, a retry or a double submit is idempotent.
   *
   * Do not call this directly: go through the chat store's `setChatPinned` /
   * `reorderPinnedChats`, which write the caches first so concurrent writers build on
   * each other's result instead of overwriting it. This only reconciles afterwards.
   */
  const setPins = useMutation<void, Error, { chats: IChatListItem[] }>({
    mutationFn: ({ chats }) =>
      ChatApi.setPins({ chat_ids: chats.map((chat) => chat.id) }),
    onSettled: () => {
      // Either way the server owns the split and `total`. Refetching settles the section
      // on what it actually stored rather than on the optimistic write — which is also
      // the rollback when the request failed.
      queryClient.invalidateQueries({ queryKey: ['chats', 'list'] })
    },
    onError: (error) => {
      handleApiErrorToast(
        error as unknown as Parameters<typeof handleApiErrorToast>[0],
      )
    },
  })

  return {
    list,
    get,
    htmlPages,
    website,
    newChat,
    sendMessage,
    update,
    extendRetention,
    remove,
    setPins,
  }
}
